import { Control, useWatch } from 'react-hook-form';

import { FieldGroup } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { OrderField } from './OrderFormFields';
import { Separator } from '@/components/ui/separator';
import i18n from '@/utils/i18n';
import { OrderFormValues } from '../schema';
import { SectionCard } from '../../components/SectionCard';
import { BusinessType } from '@/graphql/generatedTypes';

export function InfoFieldsSection({
  formControl,
}: {
  formControl: Control<OrderFormValues>;
}) {
  const businessType = useWatch({
    control: formControl,
    name: 'businessType',
  });

  const isCustomerTypeKnown = !!businessType;

  const isPrivatePerson = businessType === BusinessType.SoleProprietorship;
  const isPartnership =
    businessType === BusinessType.Partnership ||
    businessType === BusinessType.LimitedPartnership;

  return (
    <SectionCard
      title={
        isCustomerTypeKnown
          ? i18n.t('Billing:billingInfo')
          : i18n.t('Event:generalInfo')
      }
    >
      {!isCustomerTypeKnown && <GeneralInfoFields />}
      {isCustomerTypeKnown && (
        <BillingInfoFields
          isPrivatePerson={isPrivatePerson}
          isPartnership={isPartnership}
        />
      )}
    </SectionCard>
  );
}

function GeneralInfoFields() {
  return (
    <form onSubmit={(ev) => ev.preventDefault()}>
      <FieldGroup>
        <OrderField.BusinessType className="tw:md:w-[calc(50%-8px)]" />
      </FieldGroup>
    </form>
  );
}

function BillingInfoFields({
  isPrivatePerson,
  isPartnership,
}: {
  isPrivatePerson: boolean;
  isPartnership: boolean;
}) {
  return (
    <form onSubmit={(ev) => ev.preventDefault()}>
      <FieldGroup className="tw:gap-4">
        <OrderField.BusinessType className="tw:md:w-[calc(50%-8px)]" />

        {isPrivatePerson && <FieldsForPrivatePerson />}

        {isPartnership && <FieldsForPartnership />}

        {!isPrivatePerson && !isPartnership && <FieldsForCorporation />}
      </FieldGroup>
    </form>
  );
}

function FieldsForPrivatePerson() {
  return (
    <>
      <GridRow>
        <OrderField.FirstName />
        <OrderField.LastName />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber />
        <OrderField.DateOfBirth className="tw:md:w-[160px]" />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-1">
        {i18n.t('EventOrder:billingAddress')}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow className="tw:md:grid-cols-10">
        <OrderField.AddressZip className="tw:col-span-2" />
        <OrderField.AddressCity className="tw:col-span-3" />
        <OrderField.AddressCountry className="tw:col-span-2" />
        <OrderField.AddressState className="tw:col-span-3" />
      </GridRow>
    </>
  );
}

function FieldsForPartnership() {
  return (
    <>
      <GridRow>
        <OrderField.FirstName label={i18n.t('EventOrder:billingName')} />
        <OrderField.LastName label={i18n.t('EventOrder:billingSurname')} />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email label={i18n.t('EventOrder:billingEmail')} />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber
          label={i18n.t('EventOrder:companyPhoneNumber')}
        />
        <OrderField.VatId />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-1">
        {i18n.t('EventOrder:billingAddress')}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressZip />
          <OrderField.AddressCity />
        </div>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressCountry inputClassName="tw:max-w-[8rem]" />
          <OrderField.AddressState />
        </div>
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-2">
        {i18n.t('EventOrder:liablePartners')}
      </span>

      <div>
        <span className="tw:text-xs tw:text-neutral-900">
          {i18n.t('EventOrder:liablePartnersInstructions')}
          <br />
        </span>
        <span className="tw:text-xs tw:text-neutral-900 tw:font-medium">
          {i18n.t('EventOrder:liablePartnersExample')}
        </span>
        <OrderField.CorporateStructureInfo
          className="tw:mt-2"
          inputClassName="tw:text-sm"
          placeholder={i18n.t('EventOrder:liablePartnersPlaceholder')}
        />
      </div>
    </>
  );
}

function FieldsForCorporation() {
  return (
    <>
      <GridRow>
        <OrderField.FirstName label={i18n.t('EventOrder:billingName')} />
        <OrderField.LastName label={i18n.t('EventOrder:billingSurname')} />
      </GridRow>

      <GridRow>
        <OrderField.CompanyName />
        <OrderField.Email label={i18n.t('EventOrder:companyEmail')} />
      </GridRow>

      <GridRow>
        <OrderField.PhoneNumber
          label={i18n.t('EventOrder:companyPhoneNumber')}
        />
        <OrderField.VatId />
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5">
        {i18n.t('EventOrder:companyAddress')}
      </span>

      <GridRow>
        <OrderField.AddressStreet />
        <OrderField.AddressExtra />
      </GridRow>

      <GridRow>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressZip />
          <OrderField.AddressCity />
        </div>
        <div className="tw:flex tw:items-center tw:gap-4">
          <OrderField.AddressCountry inputClassName="tw:md:max-w-[13rem] tw:max-w-[10rem]" />
          <OrderField.AddressState />
        </div>
      </GridRow>

      <Separator className="tw:my-4" />

      <span className="tw:text-xl tw:font-semibold tw:-mt-1.5 tw:-mb-2">
        {i18n.t('EventOrder:authorizedSignatories')}
      </span>

      <div>
        <span className="tw:text-xs tw:text-neutral-900">
          {i18n.t('EventOrder:authorizedSignatoriesInstructions')}
          <br />
        </span>
        <span className="tw:text-xs tw:text-neutral-900 tw:font-medium">
          {i18n.t('EventOrder:authorizedSignatoriesExample')}
        </span>
        <OrderField.CorporateStructureInfo
          className="tw:mt-2"
          inputClassName="tw:text-sm"
          placeholder={i18n.t('EventOrder:authorizedSignatoriesPlaceholder')}
        />
      </div>
    </>
  );
}

function GridRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:md:grid tw:md:grid-cols-2 tw:gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
}
